import type { AnalystRatingsModel, ConsensusDirection } from '.';
import { computeTopFirms } from './computeTopFirms';
import { consensusDirection } from './formatters';
import { Duration, Instant } from '@js-joda/core';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal } from '@preact/signals-react';
import type {
    AnalystRating,
    AnalystRatingsQuery,
    AnalystRatingsResponse,
    AnalystRatingsSummary,
} from '@thinkalpha/quant-ws-client/contracts/analyst-ratings.js';
import { unmanaged } from 'inversify';
import { inject, injectable } from 'src/features/ioc';
import { getAnalystRatings } from 'src/lib/newQuant';
import type { QueryObserverModel } from 'src/models/QueryObserverModel';
import type { ReactBindings } from 'src/types/bindings';

const ONE_YEAR_DAYS = 365;
const TEN_MINUTES_MS = 10 * 60 * 1000;

const EMPTY_ITEMS: readonly AnalystRating[] = Object.freeze([]);
const EMPTY_FIRMS: readonly string[] = Object.freeze([]);

export interface AnalystRatingsModelInit {
    symbol?: ReadonlySignal<string>;
    enabled?: ReadonlySignal<boolean>;
}

@injectable()
export class AnalystRatingsModelImpl implements AnalystRatingsModel {
    readonly #symbol: ReadonlySignal<string>;
    readonly #enabled: ReadonlySignal<boolean>;

    readonly #ratingsQuery: QueryObserverModel<AnalystRatingsResponse, Error, AnalystRatingsResponse>;
    readonly #disposableStack = new DisposableStack();

    readonly #loading: ReadonlySignal<boolean>;
    readonly #error: ReadonlySignal<string | null>;
    readonly #items: ReadonlySignal<readonly AnalystRating[]>;
    readonly #summary: ReadonlySignal<AnalystRatingsSummary | null>;
    readonly #topFirms: ReadonlySignal<readonly string[]>;
    readonly #consensusDirection: ReadonlySignal<ConsensusDirection | null>;

    constructor(
        @inject('QueryObserverModelFactory')
        queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('Logger') _logger: ReactBindings['Logger'],
        @unmanaged()
        init: AnalystRatingsModelInit = {},
    ) {
        this.#symbol = init.symbol ?? signal('');
        this.#enabled = init.enabled ?? signal(false);
        const queryOptions = computed(() => {
            const symbol = this.#symbol.value;
            const enabled = this.#enabled.value;
            const isActive = enabled && Boolean(symbol);
            // `start = now - 365d`, `end` omitted (= now). Recomputed each fetch
            // (queryFn runs at request time) so the window stays current.
            return {
                queryKey: ['analyst-ratings', symbol] as readonly unknown[],
                queryFn: async (): Promise<AnalystRatingsResponse> => {
                    const payload = {
                        symbols: [symbol],
                        start: Instant.now().minus(Duration.ofDays(ONE_YEAR_DAYS)),
                    } satisfies AnalystRatingsQuery;
                    return getAnalystRatings(payload);
                },
                enabled: isActive,
                // Cache for 10 min — analyst ratings publish a few times per day
                // at most; refetching on every tab toggle would be wasteful.
                staleTime: TEN_MINUTES_MS,
            };
        });

        this.#ratingsQuery = queryObserverModelFactory<AnalystRatingsResponse, Error, AnalystRatingsResponse>(
            queryOptions,
        );
        this.#disposableStack.use(this.#ratingsQuery);

        this.#loading = computed(() => this.#ratingsQuery.signal.value.isLoading);
        this.#error = computed(() => {
            const e = this.#ratingsQuery.signal.value.error;
            return e ? e.message : null;
        });
        this.#items = computed(() => this.#ratingsQuery.signal.value.data?.items ?? EMPTY_ITEMS);
        this.#summary = computed(() => this.#ratingsQuery.signal.value.data?.summary ?? null);
        this.#topFirms = computed(() => {
            const items = this.#items.value;
            return items.length === 0 ? EMPTY_FIRMS : computeTopFirms(items);
        });
        this.#consensusDirection = computed(() => {
            const summary = this.#summary.value;
            if (!summary) return null;
            return consensusDirection(summary.consensus, summary.priorConsensus.at90d.consensus);
        });
    }

    get symbol(): ReadonlySignal<string> {
        return this.#symbol;
    }
    get enabled(): ReadonlySignal<boolean> {
        return this.#enabled;
    }
    get loading(): ReadonlySignal<boolean> {
        return this.#loading;
    }
    get error(): ReadonlySignal<string | null> {
        return this.#error;
    }
    get items(): ReadonlySignal<readonly AnalystRating[]> {
        return this.#items;
    }
    get summary(): ReadonlySignal<AnalystRatingsSummary | null> {
        return this.#summary;
    }
    get topFirms(): ReadonlySignal<readonly string[]> {
        return this.#topFirms;
    }
    get consensusDirection(): ReadonlySignal<ConsensusDirection | null> {
        return this.#consensusDirection;
    }

    refetch(): void {
        // QueryObserver exposes refetch via the underlying observer.
        void this.#ratingsQuery.queryObserver.refetch();
    }

    [Symbol.dispose](): void {
        this.#disposableStack.dispose();
    }
}
